import { zodResolver } from '@hookform/resolvers/zod';
import { useCheckPasswordStrength, useUpdatePassword } from '@hooks/useAuth';
import { getErrorMessage, getErrorTitle } from '@utils/errorMessages';
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface UpdatePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Zod schema for password validation
const updatePasswordSchema = z
  .object({
    current_password: z
      .string()
      .min(1, 'Current password is required')
      .transform(val => val.trim()),
    new_password: z
      .string()
      .min(1, 'New password is required')
      .transform(val => val.trim()),
    confirm_password: z
      .string()
      .min(1, 'Please confirm your password')
      .transform(val => val.trim()),
  })
  .refine(data => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>;

export function UpdatePasswordDialog({ open, onOpenChange }: UpdatePasswordDialogProps) {
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const updatePasswordMutation = useUpdatePassword();
  const {
    strength: passwordStrength,
    isChecking: isCheckingStrength,
    error: strengthError,
    checkStrength,
    reset: resetStrength,
    meetsRequirements,
  } = useCheckPasswordStrength();

  const form = useForm<UpdatePasswordFormData>({
    resolver: zodResolver(updatePasswordSchema as any),
    mode: 'onChange',
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const handleSubmit = async (data: UpdatePasswordFormData) => {
    setErrorMessage('');

    updatePasswordMutation.mutate(data, {
      onSuccess: () => {
        // Show success toast notification
        toast.success('Password updated successfully!', {
          duration: 5000,
          position: 'bottom-right',
        });

        // Reset form and close dialog
        form.reset();
        updatePasswordMutation.reset();
        onOpenChange(false);
      },
      onError: (err: any) => {
        const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
        setErrorMessage(
          apiMessage || err.message || 'Failed to update password. Please try again.',
        );
      },
    });
  };

  // Reset form when dialog is closed
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setErrorMessage('');
      updatePasswordMutation.reset();
      resetStrength();
    }
    onOpenChange(newOpen);
  };

  // Watch form values for password match indicator
  const newPassword = form.watch('new_password');
  const confirmPassword = form.watch('confirm_password');

  const passwordsMatch =
    newPassword &&
    confirmPassword &&
    newPassword.trim() === confirmPassword.trim() &&
    newPassword.trim().length >= 8;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Password</DialogTitle>
          <DialogDescription>
            Enter your current password and choose a new password. Your new password must meet
            security requirements.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 mt-4">
            {/* Error message display - moved to top */}
            {(updatePasswordMutation.isError || errorMessage) && (
              <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium">
                      {updatePasswordMutation.error
                        ? getErrorTitle(updatePasswordMutation.error as any, 'Update Failed')
                        : 'Update Failed'}
                    </p>
                    <p className="mt-1">
                      {errorMessage ||
                        (updatePasswordMutation.error
                          ? getErrorMessage(updatePasswordMutation.error as any)
                          : '')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Current Password Field */}
            <FormField
              control={form.control}
              name="current_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder="Enter your current password"
                        className="h-12 pr-10"
                        onChange={e => {
                          field.onChange(e);
                          setErrorMessage('');
                          updatePasswordMutation.reset();
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* New Password Field */}
            <FormField
              control={form.control}
              name="new_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        className="h-12 pr-10"
                        onChange={e => {
                          field.onChange(e);
                          setErrorMessage('');
                          updatePasswordMutation.reset();
                          // Check password strength as user types
                          checkStrength(e.target.value);
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  {/* Password Strength Indicator */}
                  <PasswordStrengthIndicator
                    strength={passwordStrength}
                    isChecking={isCheckingStrength}
                    error={strengthError}
                    className="mt-2"
                  />
                </FormItem>
              )}
            />

            {/* Confirm Password Field */}
            <FormField
              control={form.control}
              name="confirm_password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your new password"
                        className="h-12 pr-10"
                        onChange={e => {
                          field.onChange(e);
                          setErrorMessage('');
                          updatePasswordMutation.reset();
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  {passwordsMatch && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                      <Check className="h-3 w-3" />
                      Passwords match
                    </p>
                  )}
                </FormItem>
              )}
            />

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={updatePasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !form.formState.isValid ||
                  updatePasswordMutation.isPending ||
                  !meetsRequirements ||
                  isCheckingStrength
                }
              >
                {updatePasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
